import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Globe, Users, Eye, UserPlus, MousePointerClick, Loader2,
  MapPin, MonitorSmartphone, Chrome, FileText, ExternalLink, Activity,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../lib/api';
import { formatDateTime } from '../lib/utils';

const RANGES = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const StatCard = ({ icon: Icon, color, label, value, sub, testid }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-5 pb-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-slate-900" data-testid={testid}>{value}</p>
          {sub && <p className="text-xs text-slate-400">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const BreakdownList = ({ title, icon: Icon, rows, iconColor, testid }) => {
  const max = Math.max(1, ...(rows || []).map((r) => r.count));
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {(!rows || rows.length === 0) ? (
          <p className="text-sm text-slate-400 py-4 text-center">No data yet.</p>
        ) : (
          <div className="space-y-2.5" data-testid={testid}>
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm text-slate-700 truncate" title={r.key}>{r.key}</span>
                    <span className="text-xs font-semibold text-slate-500 shrink-0">{r.count}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${(r.count / max) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const WebsiteAnalytics = () => {
  const [summary, setSummary] = useState(null);
  const [breakdowns, setBreakdowns] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [recent, setRecent] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const fetchRange = useCallback(async (d) => {
    const [b, t] = await Promise.all([
      api.get(`/website-analytics/breakdowns?days=${d}`),
      api.get(`/website-analytics/timeseries?days=${d}`),
    ]);
    setBreakdowns(b.data);
    setTimeseries(t.data);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, r] = await Promise.all([
          api.get('/website-analytics/summary'),
          api.get('/website-analytics/recent?limit=50'),
        ]);
        setSummary(s.data);
        setRecent(r.data);
        await fetchRange(days);
      } catch (err) {
        console.error('Failed to load website analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRangeChange = async (d) => {
    setDays(d);
    try { await fetchRange(d); } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const today = summary?.today || { views: 0, unique_visitors: 0 };
  const win = (days === 7 ? summary?.last_7_days : days === 30 ? summary?.last_30_days : summary?.last_30_days) || {};
  const allTime = summary?.all_time || { views: 0, unique_visitors: 0 };

  return (
    <Layout>
      <div className="space-y-6" data-testid="website-analytics-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
              <Globe className="w-6 h-6 text-blue-600" />
              Website Analytics
            </h1>
            <p className="text-sm text-slate-500 mt-1">Live traffic from the Helios website — page views, visitors, sources and geography.</p>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="range-toggle">
            {RANGES.map((r) => (
              <Button
                key={r.value}
                size="sm"
                variant={days === r.value ? 'default' : 'ghost'}
                className={days === r.value ? 'bg-white shadow-sm text-slate-900 hover:bg-white' : 'text-slate-500'}
                onClick={() => onRangeChange(r.value)}
                data-testid={`range-${r.value}`}
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Headline stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="headline-stats">
          <StatCard icon={Eye} color="bg-blue-100 text-blue-600" label="Views Today" value={today.views} sub={`${today.unique_visitors} visitors`} testid="stat-today-views" />
          <StatCard icon={Activity} color="bg-emerald-100 text-emerald-600" label={`Views (${days}d)`} value={win.views ?? 0} sub={`${win.unique_visitors ?? 0} visitors`} testid="stat-window-views" />
          <StatCard icon={UserPlus} color="bg-amber-100 text-amber-600" label={`New Visitors (${days}d)`} value={win.new_visitors ?? 0} sub={`${win.sessions ?? 0} sessions`} testid="stat-window-new" />
          <StatCard icon={Users} color="bg-purple-100 text-purple-600" label="All-Time Views" value={allTime.views} sub={`${allTime.unique_visitors} visitors`} testid="stat-alltime-views" />
        </div>

        {/* Trend chart */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold font-['Manrope']">Traffic Trend</CardTitle>
            <CardDescription>Daily page views and unique visitors over the last {days} days</CardDescription>
          </CardHeader>
          <CardContent>
            {timeseries.length === 0 ? (
              <div className="text-center py-12 text-slate-400" data-testid="trend-empty">
                <Globe className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No traffic recorded in this window yet.</p>
              </div>
            ) : (
              <div className="h-72" data-testid="trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={timeseries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="visitorsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                    <Area type="monotone" dataKey="views" name="Views" stroke="#3b82f6" fill="url(#viewsGrad)" strokeWidth={2} />
                    <Area type="monotone" dataKey="unique_visitors" name="Visitors" stroke="#10b981" fill="url(#visitorsGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Breakdowns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <BreakdownList title="Top Pages" icon={FileText} iconColor="text-blue-500" rows={breakdowns?.top_pages} testid="top-pages" />
          <BreakdownList title="Referrers" icon={ExternalLink} iconColor="text-purple-500" rows={breakdowns?.top_referrers} testid="top-referrers" />
          <BreakdownList title="Countries" icon={MapPin} iconColor="text-emerald-500" rows={breakdowns?.countries} testid="countries" />
          <BreakdownList title="Cities" icon={MapPin} iconColor="text-amber-500" rows={breakdowns?.cities} testid="cities" />
          <BreakdownList title="Devices" icon={MonitorSmartphone} iconColor="text-indigo-500" rows={breakdowns?.devices} testid="devices" />
          <BreakdownList title="Browsers" icon={Chrome} iconColor="text-rose-500" rows={breakdowns?.browsers} testid="browsers" />
        </div>

        {/* Live feed */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
              <MousePointerClick className="w-5 h-5 text-blue-500" />
              Recent Page Views
            </CardTitle>
            <CardDescription>Latest 50 page views across all visitors</CardDescription>
          </CardHeader>
          <CardContent>
            {recent.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center" data-testid="recent-empty">No page views recorded yet.</p>
            ) : (
              <div className="space-y-1 max-h-[420px] overflow-y-auto" data-testid="recent-feed">
                {recent.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <Eye className="w-4 h-4 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{r.page_title || r.page}</p>
                      <p className="text-xs text-slate-400 truncate">{r.page}</p>
                    </div>
                    <div className="text-right shrink-0 hidden sm:block">
                      <p className="text-xs text-slate-500">
                        {[r.city, r.country].filter((x) => x && x !== 'Unknown').join(', ') || '—'}
                      </p>
                      <div className="flex items-center justify-end gap-1.5 mt-0.5">
                        {r.is_new_visitor && <Badge className="bg-emerald-100 text-emerald-600 border-emerald-200 text-[10px] px-1.5 py-0">New</Badge>}
                        {r.device_type && <span className="text-[10px] text-slate-400 capitalize">{r.device_type}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0 w-28 hidden md:block">
                      <p className="text-xs text-slate-400">{formatDateTime(r.event_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default WebsiteAnalytics;
